/**
 * Форматирование денег и времени.
 *
 * Деньги приходят из БД в копейках целым числом. Делим на 100 только
 * в момент отображения: промежуточная арифметика во float запрещена.
 */

export function formatMoney(kopecks: number, currency: 'RUB' | 'KZT' = 'RUB'): string {
  const major = kopecks / 100;
  // Копейки показываем только когда они есть: «290 ₽», а не «290,00 ₽».
  const hasFraction = kopecks % 100 !== 0;

  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: hasFraction ? 2 : 0,
  }).format(major);
}

/** Короткая форма без символа валюты — для компактных мест вроде «+90». */
export function formatMoneyShort(kopecks: number): string {
  const major = kopecks / 100;
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: kopecks % 100 !== 0 ? 2 : 0,
    maximumFractionDigits: kopecks % 100 !== 0 ? 2 : 0,
  }).format(major);
}

export function formatPriceDelta(kopecks: number): string {
  if (kopecks === 0) return '';
  return `+${formatMoneyShort(kopecks)} ₽`;
}

/**
 * Время слота в timezone точки, а не браузера.
 *
 * Клиент может сидеть в другом часовом поясе (приехал в город, VPN,
 * неверные настройки телефона). Показываем время так, как его назовут
 * на выдаче.
 */
export function formatSlotTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso));
}

export function formatSlotRange(
  startIso: string,
  endIso: string,
  timezone: string,
): string {
  return `${formatSlotTime(startIso, timezone)}–${formatSlotTime(endIso, timezone)}`;
}

/** «Сегодня», «Завтра» или «14 октября» — в timezone точки. */
export function formatSlotDay(iso: string, timezone: string): string {
  const dayKey = (d: Date) =>
    new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timezone,
    }).format(d);

  const target = dayKey(new Date(iso));
  const now = new Date();
  const today = dayKey(now);
  const tomorrow = dayKey(new Date(now.getTime() + 86_400_000));

  if (target === today) return 'Сегодня';
  if (target === tomorrow) return 'Завтра';

  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    timeZone: timezone,
  }).format(new Date(iso));
}

export function formatDateTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso));
}

/** «3 мин», «1 ч 12 мин» — для таймера ожидания на кухне. */
export function formatDuration(minutes: number): string {
  if (minutes < 1) return 'меньше минуты';
  if (minutes < 60) return `${minutes} мин`;

  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} ч` : `${h} ч ${m} мин`;
}

const PLURAL_RULES = new Intl.PluralRules('ru-RU');

/** plural(5, ['позиция','позиции','позиций']) → «позиций». */
export function plural(n: number, forms: [string, string, string]): string {
  const rule = PLURAL_RULES.select(n);
  switch (rule) {
    case 'one':
      return forms[0];
    case 'few':
      return forms[1];
    default:
      return forms[2];
  }
}

export function pluralize(n: number, forms: [string, string, string]): string {
  return `${n} ${plural(n, forms)}`;
}

export const ORDER_STATUS_LABEL: Record<string, string> = {
  new: 'Отправлен',
  accepted: 'Принят',
  cooking: 'Готовится',
  ready: 'Готов',
  completed: 'Выдан',
  cancelled: 'Отменён',
  rejected: 'Отклонён',
};

export const FULFILLMENT_LABEL: Record<string, string> = {
  pickup: 'Заберу сам',
  dine_in: 'На месте',
  delivery: 'Доставка',
};

export const FULFILLMENT_SHORT: Record<string, string> = {
  pickup: 'Самовывоз',
  dine_in: 'На месте',
  delivery: 'Доставка',
};

export const PAYMENT_LABEL: Record<string, string> = {
  cash_on_pickup: 'Наличными при получении',
  card_on_pickup: 'Картой при получении',
  online: 'Онлайн',
};

/** Нормализует телефон к виду, который принимает tel:. */
export function phoneHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}

/** Маска ввода телефона: +7 999 123-45-67. */
export function formatPhoneInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const normalized = digits.startsWith('8') ? `7${digits.slice(1)}` : digits;
  const d = normalized.startsWith('7') ? normalized.slice(1) : normalized;
  const t = d.slice(0, 10);

  if (t.length === 0) return '';
  if (t.length <= 3) return `+7 ${t}`;
  if (t.length <= 6) return `+7 ${t.slice(0, 3)} ${t.slice(3)}`;
  if (t.length <= 8) return `+7 ${t.slice(0, 3)} ${t.slice(3, 6)}-${t.slice(6)}`;
  return `+7 ${t.slice(0, 3)} ${t.slice(3, 6)}-${t.slice(6, 8)}-${t.slice(8)}`;
}

export function isValidPhone(formatted: string): boolean {
  return formatted.replace(/\D/g, '').length === 11;
}
